// TH 12/20/2016 -- Using json2csv. See https://github.com/zemirco/json2csv
// json2csv seems pretty solid, but one weirdness I noticed is that it internally calls
// JSON.stringify to escape data, so some characters get replaced w/ their JSON escapes.
// E.g., embedded new lines inside a value get converted into '\\n' (prints as a backslash + n),
// so that's what you see if you open the file in Excel.
// As an alternative, I tried papaparse (https://github.com/mholt/PapaParse).
// That produced the CSV I was expecting for new lines (as described in http://stackoverflow.com/a/566059).
// But I found that Excel 2016 didn't display the CSV as I expected -- for new lines, I'd only see the first line of each cell.
// So bottom line, papaparse gave no better results than json2csv when importing into Excel.
// And for data w/o new lines (which is our expected case), their output was nearly identical.
// Meanwhile papaparse is no longer actively maintained.
// So sticking w/ json2csv for now.
import * as json2csv from 'json2csv';

import TZDate from 'lib/TZDate';

export interface CSVField<TRow> {
    name : string;
    formatter : (row : TRow) => string;
}

/**
 * Format the given rows and fields as a CSV string.
 * @param rows The rows of data
 * @param fields The CSV fields
 */
export function toCSV<TRow>(rows : TRow[], fields : CSVField<TRow>[]) {
    return json2csv({
        data: rows,
        fields: fields.map(f => ({
            label: f.name,
            value: f.formatter
        }))
    });
}

/**
 * Format the given rows and fields as a CSV Blob
 * @param rows The rows of data
 * @param fields The CSV fields
 */
export function toCSVBlob<TRow>(rows : TRow[], fields : CSVField<TRow>[]) {
    const csv = toCSV(rows, fields);
    return new Blob([csv], { type: 'text/csv' });
}

/**
 * Format the given rows and fields as a CSV data URI
 * @deprcated Data URIs have length restrictions on some browsers (notably Chrome).
 * For more reliable support of large data, use `toCSV` or `toCSVBlob` instead.
 * @param rows The rows of data
 * @param fields The CSV fields
 */
export function toCSVDataUri<TRow>(rows : TRow[], fields : CSVField<TRow>[]) {
    const csv = toCSV(rows, fields);
    return 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
}

// TH 02/15/2017 -- Date formatting functions optimized for importing CSV into Excel.
// I've found that the best practice is to do date and time as separate columns and use the
// formats below. That'll cause Excel to format the incoming columns as date and time and apply
// region specific formatting.
export function ExcelDateFormat(date : TZDate) {
    return date.toJSON().substr(0, 10); // YYYY-MM-DD
}

export function ExcelTimeFormat(date : TZDate) {
    return date.toJSON().substr(11, 5); // HH:mm
}

export function ExcelDateTimeFormat(date : TZDate) {
    return ExcelDateFormat(date) + ' ' + ExcelTimeFormat(date);
}


// WEBPACK FOOTER //
// ./apps/common/services/csvExport.ts